import { Button, message, Row, Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import { DeleteOutlined } from "@ant-design/icons";

import "./styles.less";
import { useContext, useEffect, useState } from "react";
import { deletMachineApi, getConfigApi, renameMachineApi } from "../../axios/portalData";
import { useNavigate } from "react-router-dom";
import TransferMachine from "./TransferMachine";
import Dropdown from "../Dropdown/Dropdown";
import UserContext from "../../themeContext/UserContext";
import SelectedGroupContext from "../../themeContext/selectedGroupContext";
import Confirmation from "../Common/Confirmation";
import { TextEditCell } from "../../Pages/account";
import GroupDocumentContext from "../../themeContext/GroupDocumentContext";
import GroupsMachinesReFetchFlagContext from "../../themeContext/GroupsMachinesReFetchFlagContext";
import MachineDocumentContext from "../../themeContext/MachineDocumentContext";
import { MachineDocument } from "../../Types/dashboardTypes";
import { downloadFile } from "../../Common/commonFunctions";

export type MachineDetails = {
  machineName: string;
  creationDate: string;
  connectionString: string;
  serialNumber: string;
};

const getConfig = (machineSerial: string) => {
  getConfigApi(machineSerial).then((resp) => {
    downloadFile({
      data: JSON.stringify(resp, null, 4),
      fileName: `config_${machineSerial.split("_")[0]}.json`,
      fileType: "json",
    });
  });
};

const Machines: React.FC = () => {
  const navigate = useNavigate();

  const { selectedGroup, setSelectedGroup } = useContext(SelectedGroupContext);
  const { groupDocument } = useContext(GroupDocumentContext);
  const { machineDocuments } = useContext(MachineDocumentContext);
  const { setGroupsMachinesReFetchFlag } = useContext(GroupsMachinesReFetchFlagContext);
  const [groups, setGroups] = useState<string[]>([]);
  const [machinesData, setMachinesData] = useState<MachineDocument[] | undefined>(undefined);

  const { userInfo } = useContext(UserContext);
  const accountType = userInfo.accountType;
  const [transferModal, setTransferModal] = useState<boolean>(false);
  const [deleteMachineData, setDeleteMachineData] = useState<MachineDocument | undefined>(undefined);

  const onDeleteClicked = (value: MachineDocument) => {
    setDeleteMachineData(value);
  };

  const deleteMachine = () => {
    if (deleteMachineData) {
      setMachinesData(undefined);
      setDeleteMachineData(undefined);
      deletMachineApi(deleteMachineData.serialNumber)
        .then((resp) => {
          message.success(resp);
          setGroupsMachinesReFetchFlag(true);
        })
        .catch((err) => {
          console.log(err);
          message.error("Error in deleting machine");
          setMachinesData([]);
        });
    }
  };

  const renameMachine = (machineDetails: MachineDocument, newMachineName: string) => {
    if (machineDetails.machineName != newMachineName) {
      setMachinesData(undefined);

      renameMachineApi(machineDetails, newMachineName)
        .then((resp) => {
          message.success(resp);
          setGroupsMachinesReFetchFlag(true);
        })
        .catch((error) => {
          console.log(error);
          message.error(error.message);
          getMachineDetails();
        });
    }
  };

  const columns: ColumnsType<MachineDocument> = [
    {
      title: "Name",
      dataIndex: "machineName",
      render: (name, row) => <TextEditCell value={name} handleClickDone={(value) => renameMachine(row, value)} />,
    },
    {
      title: "Date Added",
      dataIndex: "creationDate",
      responsive: ["md", "lg"],
      width: 250,
    },
    {
      title: "Actions",
      align: "center",
      width: 150,
      render: (value) => <DeleteOutlined className="icon" onClick={() => onDeleteClicked(value)} />,
    },
    {
      title: "Connection String",
      dataIndex: "serialNumber",
      render: (value) => (
        <Button type="link" className="config-download" onClick={() => getConfig(value)}>
          <div className="underline-hover">Download connection details</div>
        </Button>
      ),
    },
  ];

  useEffect(() => {
    if (Object.keys(groupDocument.groupDocument.groups).length <= 1) {
      setGroups(["None"]);
    } else {
      const temp = Object.keys(groupDocument.groupDocument.groups);
      // re-orders the names so "None" is always at the top, otherwise it is alphabetical
      for (let i = 0; i < temp.length; i++) {
        if (temp[i] == "None") {
          temp.splice(i, 1);
        }
      }
      temp.unshift("None");
      setGroups(temp);
    }
  }, [groupDocument]);

  const getMachineDetails = () => {
    const machineDetails: MachineDocument[] = [];
    groupDocument?.groupDocument?.groups[selectedGroup as string]?.map((machine) => {
      machineDocuments?.map((item) => {
        if (item.serialNumber == machine) {
          machineDetails.push(item);
        }
      });
    });
    setMachinesData(machineDetails);
  };

  useEffect(() => {
    setMachinesData(undefined);
    getMachineDetails();
  }, [selectedGroup, groupDocument, machineDocuments]);

  const onClose = (refresh: boolean) => {
    if (refresh) {
      getMachineDetails();
    }
    setTransferModal(false);
  };

  return (
    <div className="settings-panel machines">
      {accountType != "free" ? (
        <Row style={{ marginBottom: "50px" }}>
          <Dropdown
            options={groups.map((e) => ({
              label: e,
              value: e,
            }))}
            title="View by Groups"
            mainClassName={"group-dropdown-settings"}
            onChange={(value) => setSelectedGroup(value as string)}
            value={selectedGroup ?? ""}
          />
        </Row>
      ) : (
        ""
      )}
      <Row>
        <Table
          loading={machinesData === undefined}
          className="settings-table machine"
          scroll={{ x: "none", y: 300 }}
          columns={columns}
          dataSource={machinesData}
          pagination={false}
          locale={{
            emptyText: machinesData ? "No Machines in this group" : "Loading Machine Data",
          }}
          rowKey={"serialNumber"}
        />
      </Row>
      <div className="need-help-text">
        Need help connecting your machine?{" "}
        <Button type="link" className="config-download" onClick={() => window.open("/", "_blank")}>
          <div className="need-help-link">Click here</div>
        </Button>
      </div>
      <Row style={{ marginTop: "50px" }} className="btn-grp-wrapper">
        <Button
          className="primary-button"
          onClick={() => {
            navigate("/dashboard/new-machine");
          }}
        >
          New Machine
        </Button>
        {accountType != "free" ? (
          <Button
            className="secondary-button"
            onClick={() => setTransferModal(true)}
            disabled={machinesData === undefined || machinesData?.length < 1}
          >
            Transfer machine(s)
          </Button>
        ) : (
          ""
        )}
      </Row>
      {transferModal ? (
        <TransferMachine
          machines={
            machinesData
              ? machinesData.map((e) => {
                  return { label: e.machineName, value: e.serialNumber };
                })
              : []
          }
          selectedGroupName={selectedGroup as string}
          onClose={(refresh: boolean) => onClose(refresh)}
          groups={groups}
        />
      ) : (
        ""
      )}
      {deleteMachineData ? (
        <Confirmation
          okText="Delete anyway"
          titleText={`Are you sure you want to delete ${deleteMachineData.machineName}?`}
          messageText="You cannot undo this action."
          onOk={() => deleteMachine()}
          onClose={() => setDeleteMachineData(undefined)}
        />
      ) : (
        ""
      )}
    </div>
  );
};
export default Machines;
